import React from "react";
import {
  MdCampaign,
  MdHelp,
  MdEmojiEvents,
  MdForum,
  MdClear,
} from "react-icons/md";
import { PostType } from "../../types/community";

interface PostFiltersProps {
  activeFilter: PostType | null;
  onFilterChange: (type: PostType | null) => void;
  postCounts?: {
    all: number;
    general: number;
    support: number;
    challenge: number;
    announcement: number;
  };
}

const PostFilters: React.FC<PostFiltersProps> = ({
  activeFilter,
  onFilterChange,
  postCounts,
}) => {
  const filters = [
    {
      type: null,
      label: "All Posts",
      icon: MdForum,
      bgColor: "bg-gray-100 dark:bg-gray-700",
      activeBgColor: "bg-biafit-primary",
      textColor: "text-gray-700 dark:text-gray-300",
      activeTextColor: "text-white",
      iconColor: "text-gray-600 dark:text-gray-400",
      activeIconColor: "text-white",
      count: postCounts?.all,
    },
    {
      type: "general" as PostType,
      label: "General",
      icon: MdForum,
      bgColor: "bg-gray-100 dark:bg-gray-700",
      activeBgColor: "bg-gray-600 dark:bg-gray-500",
      textColor: "text-gray-700 dark:text-gray-300",
      activeTextColor: "text-white",
      iconColor: "text-gray-600 dark:text-gray-400",
      activeIconColor: "text-white",
      count: postCounts?.general,
    },
    {
      type: "support" as PostType,
      label: "Support",
      icon: MdHelp,
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
      activeBgColor: "bg-blue-600",
      textColor: "text-blue-700 dark:text-blue-300",
      activeTextColor: "text-white",
      iconColor: "text-blue-600 dark:text-blue-400",
      activeIconColor: "text-white",
      count: postCounts?.support,
    },
    {
      type: "challenge" as PostType,
      label: "Challenge",
      icon: MdEmojiEvents,
      bgColor: "bg-orange-50 dark:bg-orange-900/20",
      activeBgColor: "bg-orange-600",
      textColor: "text-orange-700 dark:text-orange-300",
      activeTextColor: "text-white",
      iconColor: "text-orange-600 dark:text-orange-400",
      activeIconColor: "text-white",
      count: postCounts?.challenge,
    },
    {
      type: "announcement" as PostType,
      label: "Announcement",
      icon: MdCampaign,
      bgColor: "bg-purple-50 dark:bg-purple-900/20",
      activeBgColor: "bg-purple-600",
      textColor: "text-purple-700 dark:text-purple-300",
      activeTextColor: "text-white",
      iconColor: "text-purple-600 dark:text-purple-400",
      activeIconColor: "text-white",
      count: postCounts?.announcement,
    },
  ];

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[15px] font-medium text-gray-700 dark:text-gray-400">
          Filter by Type:
        </h3>
        {activeFilter && (
          <button
            onClick={() => onFilterChange(null)}
            className="inline-flex items-center space-x-1 text-[15px] font-medium text-biafit-primary transition-colors hover:text-biafit-secondary"
          >
            <MdClear className="h-4 w-4" />
            <span>Clear Filter</span>
          </button>
        )}
      </div>

      {/* Desktop: Horizontal layout */}
      <div className="hidden flex-wrap gap-2 md:flex">
        {filters.map((filter) => {
          const Icon = filter.icon;
          const isActive = activeFilter === filter.type;

          return (
            <button
              key={filter.label}
              onClick={() => onFilterChange(filter.type)}
              className={`
                inline-flex items-center space-x-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200 hover:scale-[102%] 
                ${
                  isActive
                    ? `${filter.activeBgColor} ${filter.activeTextColor} shadow-md`
                    : `${filter.bgColor} ${filter.textColor} hover:shadow-sm`
                }
              `}
            >
              <Icon
                className={`h-4 w-4 ${
                  isActive ? filter.activeIconColor : filter.iconColor
                }`}
              />
              <span>{filter.label}</span>
              {filter.count !== undefined && (
                <span
                  className={`
                  ml-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold
                  ${
                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300"
                  }
                `}
                >
                  {filter.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Mobile: Grid layout */}
      <div className="grid grid-cols-3 gap-2 md:hidden">
        {filters.map((filter) => {
          const Icon = filter.icon;
          const isActive = activeFilter === filter.type;

          return (
            <button
              key={filter.label}
              onClick={() => onFilterChange(filter.type)}
              className={`
                flex flex-col items-center space-y-1 rounded-md px-2 py-2 text-xs font-medium transition-all duration-200
                ${
                  isActive
                    ? `${filter.activeBgColor} ${filter.activeTextColor} shadow-md`
                    : `${filter.bgColor} ${filter.textColor} hover:shadow-sm`
                }
              `}
            >
              <Icon
                className={`h-4 w-4 ${
                  isActive ? filter.activeIconColor : filter.iconColor
                }`}
              />
              <span className="text-xs">{filter.label}</span>
              {filter.count !== undefined && (
                <span
                  className={`
                  rounded-full px-1.5 py-0.5 text-xs font-semibold
                  ${
                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300"
                  }
                `}
                >
                  {filter.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PostFilters;
